<script>
/**
 * 거래 정책 목록 페이지.
 *
 * 기능:
 * - 거래 정책 목록 조회 (서버 페이지네이션)
 * - 검색 필터: SPOT유형(GOLD/SILVER/ALL), 활성여부(Y/N)
 * - 행 클릭 → /trade-policy/:tradePolicyCode 상세 페이지 이동
 *
 * HQ(본사) 관리자 전용. 라우터 가드에서 비HQ 접근 시 차단.
 */
import { getTradePolicyList } from '@/api/tradePolicy.js'
import { getAdminInfo } from '@/utils/token.js'
import AppPagination from '@/components/common/AppPagination.vue'

export default {
  name: 'TradePolicyListPage',

  components: { AppPagination },

  data() {
    return {
      /* ── 검색 조건 ── */
      search: {
        spotType: '',
        activeYn: '',
      },

      /** SPOT유형 필터 옵션 */
      spotTypeOptions: [
        { title: '전체', value: '' },
        { title: '금(GOLD)', value: 'GOLD' },
        { title: '은(SILVER)', value: 'SILVER' },
        { title: '전체(ALL)', value: 'ALL' },
      ],

      /** 활성여부 필터 옵션 */
      activeYnOptions: [
        { title: '전체', value: '' },
        { title: '활성', value: 'Y' },
        { title: '비활성', value: 'N' },
      ],

      /* ── 목록 데이터 ── */
      items: [],

      /* ── 페이지네이션 ── */
      page: 1,
      size: 10,
      total: 0,

      /* ── UI 상태 ── */
      loading: false,
    }
  },

  mounted() {
    this.fetchList()
  },

  methods: {
    /**
     * 서버에서 거래 정책 목록을 조회한다.
     * 검색 조건 + 페이지네이션 파라미터를 전달하고 결과를 items / total에 세팅한다.
     */
    async fetchList() {
      this.loading = true
      try {
        const res = await getTradePolicyList({
          spotType: this.search.spotType || undefined,
          activeYn: this.search.activeYn || undefined,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        }
      } catch (e) {
        console.error('[TradePolicyListPage] fetchList error:', e)
      } finally {
        this.loading = false
      }
    },

    /** 검색 버튼 클릭: 페이지를 1로 리셋 후 조회 */
    handleSearch() {
      this.page = 1
      this.fetchList()
    },

    /** 초기화 버튼 클릭: 검색 조건 + 페이지 초기화 후 재조회 */
    handleReset() {
      this.search = { spotType: '', activeYn: '' }
      this.page = 1
      this.fetchList()
    },

    /** 페이지 변경 (AppPagination emit) */
    onPageChange(newPage) {
      this.page = newPage
      this.fetchList()
    },

    /** 페이지당 항목 수 변경 (AppPagination emit) */
    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
      this.fetchList()
    },

    /** 행 클릭 → 상세 페이지로 이동 */
    goToDetail(item) {
      this.$router.push(`/trade-policy/${item.tradePolicyCode}`)
    },

    /** 퍼센트 표시: 소수점 2자리 */
    formatPct(val) {
      return val != null ? Number(val).toFixed(2) + '%' : '-'
    },

    /** 날짜 포맷: YYYY-MM-DD */
    formatDate(dt) {
      return dt ? dt.substring(0, 10) : '-'
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">정책 설정</div>
        <div class="text-body-2 text-grey mt-1">금/은 거래 정책 조회 및 수정</div>
      </div>
    </div>

    <!-- 검색 영역 -->
    <v-card rounded="lg" elevation="1" class="mt-5 mb-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      </v-card-item>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" sm="3">
            <v-select
              v-model="search.spotType"
              :items="spotTypeOptions"
              item-title="title"
              item-value="value"
              label="현물구분"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="search.activeYn"
              :items="activeYnOptions"
              item-title="title"
              item-value="value"
              label="활성여부"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
        </v-row>

        <div class="d-flex justify-end ga-2 mt-4 border-t-thin pt-4">
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- 목록 카드 -->
    <v-card rounded="lg" elevation="1">
      <v-card-item class="py-3">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          거래 정책 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
        </v-card-title>
      </v-card-item>

      <!-- 로딩 -->
      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <v-table density="compact" class="border-t">
        <thead>
          <tr>
            <th class="text-center">번호</th>
            <th class="text-center">정책코드</th>
            <th class="text-center">정책명</th>
            <th class="text-center">현물구분</th>
            <th class="text-center">상한가</th>
            <th class="text-center">하한가</th>
            <th class="text-center">활성여부</th>
            <th class="text-center">적용시작일</th>
            <th class="text-center">적용종료일</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in items"
            :key="item.tradePolicyCode"
            style="cursor: pointer;"
            class="policy-row"
            @click="goToDetail(item)"
          >
            <td class="text-center text-caption text-grey">
              {{ total - (page - 1) * size - index }}
            </td>
            <td class="text-center text-caption text-grey-darken-1">{{ item.tradePolicyCode }}</td>
            <td class="text-center font-weight-medium">{{ item.policyName || '-' }}</td>
            <td class="text-center">
              <v-chip
                v-if="item.spotType"
                size="x-small"
                :color="item.spotType === 'GOLD' ? 'amber' : item.spotType === 'SILVER' ? 'grey' : 'primary'"
                variant="tonal"
                label
              >
                {{ item.spotType }}
              </v-chip>
              <span v-else class="text-caption text-grey">-</span>
            </td>
            <td class="text-center text-caption">{{ formatPct(item.upperLimitPct) }}</td>
            <td class="text-center text-caption">{{ formatPct(item.lowerLimitPct) }}</td>
            <td class="text-center">
              <v-chip
                v-if="item.activeYn"
                size="x-small"
                :color="item.activeYn === 'Y' ? 'success' : 'grey'"
                variant="tonal"
                label
              >
                {{ item.activeYn === 'Y' ? '활성' : '비활성' }}
              </v-chip>
              <span v-else class="text-caption text-grey">-</span>
            </td>
            <td class="text-center text-caption">{{ formatDate(item.effectiveFrom) }}</td>
            <td class="text-center text-caption">{{ formatDate(item.effectiveTo) }}</td>
          </tr>

          <!-- 데이터 없음 -->
          <tr v-if="!loading && items.length === 0">
            <td colspan="9" class="text-center text-grey py-10">
              <v-icon size="40" class="d-block mx-auto mb-2" color="grey-lighten-2">
                mdi-cog-outline
              </v-icon>
              검색 결과가 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>

      <!-- 페이지네이션 -->
      <div class="px-4 pb-4">
        <AppPagination
          :total="total"
          :page="page"
          :size="size"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </v-card>
  </div>
</template>

<style scoped>
.policy-row:hover {
  background-color: rgba(205, 178, 122, 0.06);
}
</style>
